/**
 * FormField, TextField, PasswordField and Checkbox.
 *
 * `FormField` owns the parts a field always needs and that are always got wrong:
 * a persistent label wired by `htmlFor`, help text and an error message both
 * referenced from `aria-describedby`, and `aria-invalid` on the control itself.
 * A screen reader therefore hears the label, the requirement and the failure
 * without the user having to hunt for them.
 */

import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Icon } from './Icon';

export interface FormFieldProps {
  label: string;
  htmlFor: string;
  help?: ReactNode;
  error?: string;
  required?: boolean;
  children: ReactNode;
  describedBy?: string;
}

export function FormField({
  label,
  htmlFor,
  help,
  error,
  required,
  children,
}: FormFieldProps) {
  return (
    <div className="mm-field">
      <label className="mm-field__label" htmlFor={htmlFor}>
        {label}
        {required ? (
          <span className="mm-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children}
      {help ? (
        <p className="mm-field__help" id={`${htmlFor}-help`}>
          {help}
        </p>
      ) : null}
      {error ? (
        // role="alert" so a failure is announced when it appears, not only when
        // the field is next focused.
        <p className="mm-field__error" id={`${htmlFor}-error`} role="alert">
          <Icon name="alert" size={15} />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'> {
  label: string;
  help?: ReactNode;
  error?: string;
  id?: string;
}

export function TextField({ label, help, error, id, required, ...rest }: TextFieldProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const described = [help ? `${fieldId}-help` : '', error ? `${fieldId}-error` : '']
    .filter(Boolean)
    .join(' ');

  return (
    <FormField label={label} htmlFor={fieldId} help={help} error={error} required={required}>
      <input
        id={fieldId}
        className={`mm-input ${error ? 'mm-input--invalid' : ''}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={described || undefined}
        required={required}
        {...rest}
      />
    </FormField>
  );
}

/**
 * A password rule and where it stands.
 *
 * `pending` exists because some rules cannot honestly be evaluated in the
 * browser. "Not a password everyone uses" is checked against Django's common
 * password list on the server; guessing at it client-side would either show a
 * green tick that the server then contradicts, or withhold one that was already
 * earned. Pending says the true thing: not judged yet.
 */
export type RequirementState = 'met' | 'unmet' | 'pending';

export interface PasswordRequirement {
  label: string;
  state: RequirementState;
  /** Shown next to a pending rule so the wait is explained, not mysterious. */
  pendingNote?: string;
}

export interface PasswordFieldProps extends Omit<TextFieldProps, 'type'> {
  /**
   * Shown as a checklist. Deliberately not a strength meter: a coloured bar is a
   * guess dressed as a measurement, and these are the rules the server actually
   * enforces.
   */
  requirements?: PasswordRequirement[];
  autoComplete?: 'current-password' | 'new-password';
}

export function PasswordField({
  label,
  help,
  error,
  id,
  required,
  requirements,
  autoComplete = 'current-password',
  ...rest
}: PasswordFieldProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const [revealed, setRevealed] = useState(false);
  const listId = `${fieldId}-requirements`;
  const described = [
    help ? `${fieldId}-help` : '',
    error ? `${fieldId}-error` : '',
    requirements?.length ? listId : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <FormField label={label} htmlFor={fieldId} help={help} error={error} required={required}>
      <div className="mm-input-wrap">
        <input
          id={fieldId}
          type={revealed ? 'text' : 'password'}
          className={`mm-input mm-input--with-affordance ${error ? 'mm-input--invalid' : ''}`}
          // Password managers are a security feature, not an inconvenience.
          autoComplete={autoComplete}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          aria-describedby={described || undefined}
          required={required}
          {...rest}
        />
        <button
          type="button"
          className="mm-input-affordance"
          onClick={() => setRevealed((value) => !value)}
          aria-label={revealed ? 'Hide password' : 'Show password'}
          aria-pressed={revealed}
        >
          <Icon name={revealed ? 'eyeOff' : 'eye'} size={19} />
        </button>
      </div>

      {requirements?.length ? (
        <ul className="mm-requirements" id={listId}>
          {requirements.map((requirement) => (
            <li
              key={requirement.label}
              className={`mm-requirement mm-requirement--${requirement.state}`}
            >
              {/* Three distinct glyphs, not three shades: met, unmet and pending
                  must be distinguishable without colour vision. */}
              <span className="mm-requirement__mark" aria-hidden="true">
                {requirement.state === 'met' ? (
                  <Icon name="check" size={13} />
                ) : requirement.state === 'pending' ? (
                  '?'
                ) : (
                  '\u00b7'
                )}
              </span>
              <span>{requirement.label}</span>
              {/* The visible note is real text a screen reader already reads, so
                  the hidden status stays short rather than repeating it. */}
              <span className="mm-visually-hidden">
                {requirement.state === 'met'
                  ? ' \u2014 met'
                  : requirement.state === 'pending'
                    ? ' \u2014 pending'
                    : ' \u2014 not yet met'}
              </span>
              {requirement.state === 'pending' && requirement.pendingNote ? (
                <span className="mm-requirement__note">{requirement.pendingNote}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </FormField>
  );
}

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'type'> {
  label: ReactNode;
}

export function Checkbox({ label, id, ...rest }: CheckboxProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <label className="mm-checkbox" htmlFor={fieldId}>
      <input id={fieldId} type="checkbox" {...rest} />
      <span className="mm-checkbox__box" aria-hidden="true">
        <Icon name="check" size={14} />
      </span>
      <span className="mm-checkbox__label">{label}</span>
    </label>
  );
}
